import { Component, ChangeDetectionStrategy, computed, inject } from '@angular/core';
import { TranslocoModule } from '@jsverse/transloco';

import { ClipRecordingService } from '../../services/clip-recording.service';

/**
 * Píldora flotante de "grabando…", montada UNA vez en `AppComponent` (fuera del `<router-outlet>`)
 * para que sobreviva a cualquier cambio de módulo — la grabación en sí vive en
 * {@link ClipRecordingService}, un singleton `root`, así que este componente solo la refleja: no
 * hay `MediaRecorder` ni stream aquí que un `ngOnDestroy` pudiera cortar.
 */
@Component({
  selector: 'app-clip-recording-indicator',
  standalone: true,
  imports: [TranslocoModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (recording.step() === 'recording') {
      <div class="fixed bottom-5 right-5 z-99999 flex items-center gap-2.5 rounded-2xl bg-gray-900 text-white shadow-lg px-4 py-3">
        <span class="w-2 h-2 rounded-full flex-shrink-0" [class]="recording.isPaused() ? 'bg-warning-500' : 'bg-error-500 animate-pulse'"></span>
        <span class="text-xs font-semibold tabular-nums" data-test="contador-grabacion">{{ formattedElapsed() }}</span>
        <span class="text-xs text-gray-300 max-w-[140px] truncate">{{ recording.title() }}</span>
        <!-- Silenciar el micrófono sin cortar la grabación (RF-004). Sólo aparece si hay
             micrófono: el usuario pudo negar el permiso, y un botón que no apaga nada confunde
             más de lo que ayuda. En silencio pasa a rojo, porque «mudo» es un estado en el que
             se puede estar hablando sin saberlo. -->
        @if (recording.hasMic()) {
          <button type="button" class="flex items-center justify-center w-7 h-7 rounded-lg"
                  [class]="recording.micMuted() ? 'bg-error-500 hover:bg-error-600' : 'bg-white/10 hover:bg-white/20'"
                  [attr.aria-pressed]="recording.micMuted()"
                  [title]="(recording.micMuted() ? 'nexus.collaboration.reactivarMicrofono' : 'nexus.collaboration.silenciarMicrofono') | transloco"
                  data-test="silenciar-microfono"
                  (click)="recording.toggleMic()">
            <i class="bi text-sm" [class.bi-mic-mute-fill]="recording.micMuted()" [class.bi-mic-fill]="!recording.micMuted()"></i>
          </button>
        }
        <button type="button" class="flex items-center justify-center w-7 h-7 rounded-lg bg-white/10 hover:bg-white/20"
                [title]="(recording.isPaused() ? 'nexus.collaboration.reanudarGrabacion' : 'nexus.collaboration.pausarGrabacion') | transloco"
                data-test="pausar-grabacion"
                (click)="recording.togglePause()">
          <i class="bi text-sm" [class.bi-play-fill]="recording.isPaused()" [class.bi-pause-fill]="!recording.isPaused()"></i>
        </button>
        <button type="button" class="flex items-center justify-center w-7 h-7 rounded-lg bg-error-500 hover:bg-error-600"
                [title]="'nexus.collaboration.detenerGrabacion' | transloco" data-test="detener-grabacion" (click)="recording.stop()">
          <i class="bi bi-stop-circle-fill text-sm"></i>
        </button>
      </div>
    }
  `,
})
export class ClipRecordingIndicatorComponent {
  readonly recording = inject(ClipRecordingService);

  readonly formattedElapsed = computed(() => {
    const total = this.recording.elapsedSeconds();
    const mm = Math.floor(total / 60).toString().padStart(2, '0');
    const ss = (total % 60).toString().padStart(2, '0');
    return `${mm}:${ss}`;
  });
}
